/**
 * roxin-age-gate.js
 * ---------------------------------------------------------------------------
 * Comportamento da verificação de idade.
 *
 * A DECISÃO de abrir ou não já foi tomada por um script inline dentro da
 * seção, que roda antes da primeira pintura (senão a tela de idade piscaria
 * em toda visita de quem já confirmou). Este arquivo cuida do resto:
 * cliques, foco, teclado e gravação da resposta.
 *
 * Acessibilidade: enquanto o diálogo está aberto, o foco fica preso dentro
 * dele — Tab não escapa para a página atrás, que continua visualmente
 * presente mas inacessível. Sem isso, quem navega por teclado "sairia" do
 * diálogo sem ter respondido.
 */
(() => {
  if (window.__roxinAgeGateReady) return;
  window.__roxinAgeGateReady = true;

  const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  const init = (root = document) => {
    const gates = root.querySelectorAll('[data-rx-age]:not([data-rx-age-ready])');

    gates.forEach((gate) => {
      gate.setAttribute('data-rx-age-ready', 'true');

      const confirmBtn = gate.querySelector('[data-rx-age-confirm]');
      const denyBtn = gate.querySelector('[data-rx-age-deny]');
      const deniedBox = gate.querySelector('[data-rx-age-denied]');

      const close = () => {
        gate.hidden = true;
        document.documentElement.classList.remove('rx-age-open');
        document.removeEventListener('keydown', onKeydown, true);
      };

      const remember = () => {
        const ms = parseInt(gate.dataset.rememberMs, 10);
        if (isNaN(ms)) return;
        try {
          window.localStorage.setItem(gate.dataset.storageKey, String(Date.now() + ms));
        } catch (e) {
          /*
            localStorage indisponível (navegação privada, cookies bloqueados).
            Não é erro: a pessoa segue navegando normalmente nesta visita, só
            será perguntada de novo na próxima. Melhor perguntar demais do que
            liberar sem verificação.
          */
        }
      };

      /* Mantém o foco dentro do diálogo enquanto ele estiver aberto. */
      const onKeydown = (event) => {
        if (gate.hidden) return;

        // Esc não fecha de propósito: sair sem responder anularia a verificação.
        if (event.key === 'Escape') {
          event.preventDefault();
          return;
        }

        if (event.key !== 'Tab') return;

        const items = Array.from(gate.querySelectorAll(FOCUSABLE)).filter(
          (el) => !el.disabled && el.offsetParent !== null
        );
        if (!items.length) return;

        const first = items[0];
        const last = items[items.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      if (confirmBtn) {
        confirmBtn.addEventListener('click', () => {
          remember();
          close();
        });
      }

      if (denyBtn) {
        denyBtn.addEventListener('click', () => {
          const action = gate.dataset.denyAction;
          const url = gate.dataset.denyUrl;

          if (action === 'redirect' && url) {
            window.location.href = url;
            return;
          }

          // Mostra a mensagem e remove as opções: não há como "tentar de novo".
          if (deniedBox) deniedBox.hidden = false;
          if (confirmBtn) confirmBtn.hidden = true;
          denyBtn.hidden = true;
          if (deniedBox) deniedBox.setAttribute('tabindex', '-1');
          if (deniedBox) deniedBox.focus();
        });
      }

      if (!gate.hidden) {
        document.addEventListener('keydown', onKeydown, true);
        // Leva o foco para o diálogo; sem isso o teclado continuaria na página atrás.
        if (confirmBtn) {
          window.requestAnimationFrame(() => confirmBtn.focus());
        }
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  // O editor de temas recarrega seções isoladamente.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
